<template>
  <div :class="classes()">
    <Headline size="m" isViewHeadline>{{ $t('applications.title') }}</Headline>

    <IntroBanner
      :headline="$t('applications.introBanner.headline')"
      :copy="$t('applications.introBanner.copy')"
      :bottomSpace="40"
      :localStorage="LS_APPLICATIONS_INFO_BANNER"
    />

    <Widget :headline="$t('applications.subHeadine1')">
      <InViewAnimation>
        <Teaser
          slot="0"
          :el="el"
          id="erstantrag"
          first
          @onTrackingClick="trackClick"
          :documents="initialApplications"
          :token="token"
        />
        <Teaser
          slot="1"
          :el="el"
          id="hoeherstufung"
          @onTrackingClick="trackClick"
          :documents="upgradeApplications"
          :token="token"
        />
      </InViewAnimation>
    </Widget>

    <InViewAnimation :delay="2">
      <IntroBanner
        slot="0"
        :headline="$t('applications.introBanner2.headline')"
        :copy="$t('applications.introBanner2.copy')"
        highlightColor="supportWarning"
        iconName="error-warning-fill"
        notCloseable
        :class="`${el}__introBanner`"
      />
    </InViewAnimation>

    <Widget :headline="$t('applications.box2.subline')">
      <InViewAnimation :delay="3">
        <Teaser slot="0" :el="el" id="pflegehilfsmittel" first @onTrackingClick="trackClick" />
        <Teaser slot="1" :el="el" id="hausnotruf" @onTrackingClick="trackClick" />
        <Teaser slot="2" :el="el" id="wohnumfeldverbesserungen" @onTrackingClick="trackClick" />
        <Teaser slot="3" :el="el" id="entlastungsbetrag" @onTrackingClick="trackClick" />
      </InViewAnimation>
    </Widget>

    <IntroBanner
      :headline="$t('applications.introBanner3.headline')"
      :copy="$t('applications.introBanner3.copy')"
      highlightColor="supportWarning"
      iconName="error-warning-fill"
      notCloseable
      :class="`${el}__introBanner`"
    />

    <Widget :headline="$t('applications.box4.headline')">
      <Teaser :el="el" id="verhinderungspflege" first @onTrackingClick="trackClick" />
      <Teaser :el="el" id="kurzzeitpflege" @onTrackingClick="trackClick" />
      <Teaser :el="el" id="tagesundnachtpflege" @onTrackingClick="trackClick" />
    </Widget>

    <IntroBanner
      :headline="$t('applications.introBanner4.headline')"
      :copy="$t('applications.introBanner4.copy')"
      highlightColor="supportWarning"
      iconName="error-warning-fill"
      notCloseable
      :class="`${el}__introBanner`"
    />

    <Widget :headline="$t('applications.box3.subline')">
      <Teaser :el="el" id="pflegegeld" first @onTrackingClick="trackClick" />
      <Teaser :el="el" id="pflegesachleistungen" @onTrackingClick="trackClick" />
      <Teaser :el="el" id="kombinationsleistung" @onTrackingClick="trackClick" />
    </Widget>

    <EdithcarePopUp
      v-if="edithcarePrivacyStatement"
      @onTrackingClick="trackClick"
      @close="updateEdithcareToken"
      :edithcarePrivacyStatement="edithcarePrivacyStatement"
    />
  </div>
</template>

<script>
import Headline from '@/objects/Headline';
import Widget from '@/objects/Widget';
import IntroBanner from '@/components/IntroBanner';
import InViewAnimation from '@/components/InViewAnimation';
import EdithcarePopUp from '@/components/EdithcarePopUp/EdithcarePopUp';
import Teaser from './subcomponents/Teaser';

import Vue from 'vue';
import {
  ROUTE_API_EDITHCARE,
  ROUTE_API_GET_EDITHCARE_STATEMENT,
  ROUTE_API_GET_EDITHCARE_USER_CONSENT,
  ROUTE_GET_EDITHCARE_DOCUMENTS,
} from '@/constants/apiRoutes';
import { LS_APPLICATIONS_INFO_BANNER } from '@/constants/storage';
import { mapFieldsApplication, mapFieldsContentSheet, mapFieldsHealth } from '@/store/mapFields';
import { APPLICATION_REQUEST } from '@/store/actions/application';

import { createTrackingData } from '@/helpers/tracking/utils/helpers';

export default {
  name: 'Applications',
  props: {},
  components: {
    EdithcarePopUp,
    Headline,
    IntroBanner,
    InViewAnimation,
    Widget,
    Teaser,
  },

  watch: {
    '$store.state.application.openEdithCarePopup': function () {
      if (!this.openEdithCarePopup) {
        this.updateEdithcareToken();
      }
    },
  },

  data() {
    return {
      initialApplications: [],
      upgradeApplications: [],
      edithcarePrivacyStatement: null,
      LS_APPLICATIONS_INFO_BANNER,
      token: null,
      el: 'v-applications',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  computed: {
    ...mapFieldsHealth(['healthUuid']),
    ...mapFieldsContentSheet(['contentSheetId', 'contentSheetIsOpen']),
    ...mapFieldsApplication(['openEdithCarePopup']),
  },
  created() {
    this.updateEdithcareToken();
    this.getEdithcarePrivacy();
  },

  methods: {
    updateEdithcareToken() {
      Vue.axios
        .get(ROUTE_API_GET_EDITHCARE_USER_CONSENT)
        .then((response) => {
          if (response.data && response.data.consent === true) {
            Vue.axios
              .post(ROUTE_API_EDITHCARE)
              .then((res) => {
                if (res.status === 200) {
                  this.token = res.data.accessToken;
                  this.getDocuments();
                }
              })
              .catch(() => {
                this.token = null;
              });
          }
        })
        .catch(() => {
          this.token = null;
        });
    },

    getDocuments() {
      Vue.axios
        .post(ROUTE_GET_EDITHCARE_DOCUMENTS, {
          personInNeedOfCareUuid: this.healthUuid,
          token: this.token,
        })
        .then((response) => {
          const documents = response.data.docs;
          if (documents && documents.length > 0) {
            this.initialApplications = documents.filter(
              (item) => item.submission_type === 'firstapplication'
            );
            this.upgradeApplications = documents.filter(
              (item) => item.submission_type === 'carelevelupgrade'
            );
          }
          this.$store.dispatch(APPLICATION_REQUEST);
        })
        .catch(() => {
          this.initialApplications = [];
          this.upgradeApplications = [];
        });
    },

    getEdithcarePrivacy() {
      Vue.axios
        .get(ROUTE_API_GET_EDITHCARE_STATEMENT)
        .then((response) => {
          this.edithcarePrivacyStatement = response.data.edithCareStatement;
        })
        .catch(() => {
          this.edithcarePrivacyStatement = null;
        });
    },

    trackClick(labelText, element) {
      const product = 'Antrag';
      const label = product + labelText;
      createTrackingData(product, label, element);
    },
  },
};
</script>
